import {
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
  PlusOutlined
} from "@ant-design/icons"
import type { TableProps } from "antd"
import {
  Alert,
  App,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Popconfirm,
  Result,
  Skeleton,
  Table,
  Tag,
  Typography
} from "antd"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { useAppSelector } from "../../../../../../hooks/use-app-selector.ts"
import { useLoadOnScroll } from "../../../../../../hooks/use-load-on-scroll.ts"
import { getFormatDate } from "../../../../../../utils/date/get-date-safe.ts"
import {
  deleteProjectDocument,
  getProjectDocuments
} from "../../../../api/project-documents.services.ts"
import type { ProjectDocument } from "../../../../types/project-documents.types.ts"
import { UploadDocumentModal } from "./UploadDocumentModal.tsx"

const { Text } = Typography

const PAGE_SIZE = 50

function ProjectViewDocumentsSection() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const projectId = useParams()?.id as string
  const moduleDocs = useAppSelector(
    (state) => state.auth?.permissions?.codes?.projects?.docs
  )
  const canUpload = !!moduleDocs?.project_doc_create
  const canDelete = !!moduleDocs?.project_doc_delete

  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [documents, setDocuments] = useState<ProjectDocument[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  // Only the first load replaces the whole card with a skeleton; a later one
  // must not unmount the search box the user is typing in.
  const [hasLoaded, setHasLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)

  const observer = useRef<IntersectionObserver | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  // Only the newest request may write, so a slow page-1 cannot overwrite page-2.
  const requestId = useRef(0)

  const load = useCallback(
    async (nextPage: number, term: string) => {
      const id = ++requestId.current
      setIsLoading(true)
      setHasError(false)

      const response = await getProjectDocuments({
        lead: projectId,
        page: nextPage,
        page_size: PAGE_SIZE,
        ...(term ? { q: term } : {})
      })

      if (id !== requestId.current) return

      if (!response.ok || !response.data) {
        setHasError(true)
        setIsLoading(false)
        setHasLoaded(true)
        return
      }

      const { results, count } = response.data
      setTotal(count ?? 0)
      setDocuments((current) =>
        nextPage === 1 ? (results ?? []) : [...current, ...(results ?? [])]
      )
      setPage(nextPage)
      setIsLoading(false)
      setHasLoaded(true)
    },
    [projectId]
  )

  useEffect(() => {
    if (!projectId) return
    const timer = setTimeout(() => void load(1, search), search ? 300 : 0)
    return () => clearTimeout(timer)
  }, [projectId, search, load])

  const handleDelete = useCallback(
    async (document: ProjectDocument) => {
      setDeletingId(document.id)
      const response = await deleteProjectDocument(document.id)
      setDeletingId(null)

      if (!response.ok) {
        message.error(t("projectDocuments.delete.failed"))
        return
      }

      message.success(
        t("projectDocuments.delete.succeeded", { name: document.file_name })
      )
      void load(1, search)
    },
    [load, message, search, t]
  )

  const hasMore = documents.length < total

  // A new identity here tears down and rebuilds the IntersectionObserver, which
  // on a search box means once per keystroke.
  const observerCallback = useCallback(() => {
    if (hasMore && !isLoading) void load(page + 1, search)
  }, [hasMore, isLoading, load, page, search])

  useLoadOnScroll({
    dependency: `${documents.length}-${hasMore}`,
    childrenRef: sentinelRef,
    observer,
    observerCallback
  })

  const columns = useMemo<TableProps<ProjectDocument>["columns"]>(
    () => [
      {
        title: t("projectDocuments.columns.name"),
        dataIndex: "file_name",
        key: "file_name",
        align: "left",
        render: (fileName: string) => (
          <Flex align={"center"} gap={"small"}>
            <FileTextOutlined />
            <Text ellipsis={{ tooltip: fileName }}>{fileName}</Text>
          </Flex>
        )
      },
      {
        title: t("projectDocuments.columns.type"),
        key: "file_type",
        render: (_, document) =>
          document.file_type_name ? (
            <Tag>{document.file_type_name}</Tag>
          ) : (
            <Text type={"secondary"}>—</Text>
          )
      },
      {
        title: t("projectDocuments.columns.version"),
        dataIndex: "version",
        key: "version",
        render: (version: number) => `v${version}`
      },
      {
        title: t("projectDocuments.columns.uploadedBy"),
        dataIndex: "uploaded_by",
        key: "uploaded_by",
        render: (uploadedBy: string | null) =>
          uploadedBy || <Text type={"secondary"}>—</Text>
      },
      {
        title: t("projectDocuments.columns.date"),
        dataIndex: "created",
        key: "created",
        render: (created: string) => getFormatDate(created) ?? "—"
      },
      {
        title: t("projectDocuments.columns.actions"),
        key: "actions",
        width: 110,
        render: (_, document) => (
          <Flex align={"center"} justify={"center"}>
            <Button
              type={"text"}
              icon={<DownloadOutlined />}
              href={document.file}
              target={"_blank"}
              rel={"noreferrer"}
              aria-label={t("projectDocuments.download", {
                name: document.file_name
              })}
            />
            {canDelete && (
              <Popconfirm
                title={t("projectDocuments.delete.title")}
                description={t("projectDocuments.delete.description", {
                  name: document.file_name
                })}
                okText={t("projectDocuments.delete.confirm")}
                cancelText={t("projectDocuments.delete.cancel")}
                okButtonProps={{ danger: true }}
                onConfirm={() => void handleDelete(document)}
              >
                <Button
                  type={"text"}
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingId === document.id}
                  aria-label={t("projectDocuments.delete.action", {
                    name: document.file_name
                  })}
                />
              </Popconfirm>
            )}
          </Flex>
        )
      }
    ],
    [canDelete, deletingId, handleDelete, t]
  )

  const isEmpty = !documents.length

  if (hasError && !documents.length) {
    return (
      <Card size={"small"}>
        <Result
          status={"error"}
          title={t("projectDocuments.error.title")}
          subTitle={t("projectDocuments.error.description")}
          extra={
            <Button type={"primary"} onClick={() => void load(1, search)}>
              {t("projectDocuments.error.retry")}
            </Button>
          }
        />
      </Card>
    )
  }

  if (!hasLoaded) {
    return (
      <Card size={"small"}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    )
  }

  return (
    <Card
      size={"small"}
      title={t("projectDocuments.title")}
      extra={
        <Flex align={"center"} gap={"middle"}>
          {!isEmpty && (
            <Text type={"secondary"}>
              {t("projectDocuments.count", { shown: documents.length, total })}
            </Text>
          )}
          {canUpload && (
            <Button
              type={"primary"}
              icon={<PlusOutlined />}
              onClick={() => setIsUploadOpen(true)}
            >
              {t("projectDocuments.upload.action")}
            </Button>
          )}
        </Flex>
      }
    >
      <Flex vertical gap={"middle"}>
        <Input.Search
          allowClear
          value={search}
          placeholder={t("projectDocuments.searchPlaceholder")}
          onChange={(event) => setSearch(event.target.value)}
          className={"max-w-sm"}
        />

        {isEmpty ? (
          <Empty
            image={<FileTextOutlined className={"text-4xl"} />}
            description={
              <Flex vertical gap={"small"}>
                <Text strong>
                  {search
                    ? t("projectDocuments.empty.noMatchesTitle")
                    : t("projectDocuments.empty.title")}
                </Text>
                <Text type={"secondary"}>
                  {search
                    ? t("projectDocuments.empty.noMatchesDescription")
                    : t("projectDocuments.empty.description")}
                </Text>
              </Flex>
            }
          >
            {search ? (
              <Button onClick={() => setSearch("")}>
                {t("projectDocuments.empty.clearSearch")}
              </Button>
            ) : (
              canUpload && (
                <Button type={"primary"} onClick={() => setIsUploadOpen(true)}>
                  {t("projectDocuments.empty.upload")}
                </Button>
              )
            )}
          </Empty>
        ) : (
          <>
            <Table<ProjectDocument>
              rowKey={"id"}
              size={"small"}
              columns={columns}
              dataSource={documents}
              pagination={false}
              scroll={{ x: "max-content" }}
            />
            <div ref={sentinelRef} />
            {isLoading && <Skeleton active paragraph={{ rows: 2 }} />}
            {hasError && (
              <Alert
                type={"error"}
                showIcon
                message={t("projectDocuments.error.moreFailed")}
                action={
                  <Button
                    size={"small"}
                    onClick={() => void load(page + 1, search)}
                  >
                    {t("projectDocuments.error.retry")}
                  </Button>
                }
              />
            )}
          </>
        )}
      </Flex>

      {isUploadOpen && (
        <UploadDocumentModal
          projectId={projectId}
          onClose={() => setIsUploadOpen(false)}
          onUploaded={() => void load(1, search)}
        />
      )}
    </Card>
  )
}

export default ProjectViewDocumentsSection
